import { ReactNode, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Loader2, Search, XCircle } from "lucide-react";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Card, CardContent } from "../../../ui/card";
import { cn } from "../../../../utils/utils";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../StorageHelper";
import CardPagination from "../../CardPagination";
import "../../Cloud/CloudStatus.css";
import "../../Cloud/CloudMetadata.css";
import "../../ResourceRequests/ResourceRequests.css";
import "./SaasApps.css";
import { BreakdownItem, PermissionLevel, RiskLevel } from "./saasTypes";
import { ConnectionTone, formatNumber, PERMISSION_STYLES, RISK_STYLES, humanize } from "./saasFormat";

export function useSaasTenant() {
    const [tenantName, setTenantName] = useState<string>(() => myLocalStorage.getItem("latestTenant")?.tenantName || "");

    useEffect(() => {
        if (tenantName) return;
        let active = true;
        axios
            .get(`${NGROK}/api/get-all-domains`)
            .then((response) => {
                const first = Array.isArray(response.data) ? response.data[0]?.tenantName : undefined;
                if (active && first) setTenantName(first);
            })
            .catch(() => undefined);
        return () => {
            active = false;
        };
    }, [tenantName]);

    return tenantName;
}

export function HeroStat({
    icon,
    value,
    label,
    tone = "blue",
    onClick,
}: {
    icon: ReactNode;
    value: ReactNode;
    label: string;
    tone?: "blue" | "green" | "red" | "amber";
    onClick?: () => void;
}) {
    return (
        <div className={cn("cs-hero-card", onClick && "cursor-pointer")} onClick={onClick}>
            <div className={`cs-hero-icon ${tone}`}>{icon}</div>
            <div className="cs-hero-info min-w-0">
                <span className="cs-hero-value">{value}</span>
                <span className="cs-hero-label truncate">{label}</span>
            </div>
        </div>
    );
}

export function SectionCard({
    title,
    subtitle,
    icon,
    actions,
    children,
    className,
    bodyClassName,
    scroll = false,
}: {
    title: ReactNode;
    subtitle?: ReactNode;
    icon?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
    className?: string;
    bodyClassName?: string;
    scroll?: boolean;
}) {
    return (
        <Card className={cn("cloud-card-surface rounded-2xl overflow-hidden flex flex-col", className)}>
            <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3 border-b border-border">
                <div className="flex items-start gap-3 min-w-0">
                    {icon && (
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">{icon}</div>
                    )}
                    <div className="min-w-0">
                        <h3 className="font-semibold text-sm leading-tight">{title}</h3>
                        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                    </div>
                </div>
                {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
            </div>
            <CardContent className={cn("p-4 flex-1 min-h-0", scroll && "overflow-y-auto", bodyClassName)}>{children}</CardContent>
        </Card>
    );
}

export function MetaTag({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <Badge variant="secondary" className={cn("cloud-metadata-tag text-[11px] h-5 font-normal", className)}>
            {children}
        </Badge>
    );
}

export function RiskBadge({ level, className }: { level?: RiskLevel | null; className?: string }) {
    if (!level) return null;
    return (
        <Badge variant="outline" className={cn("text-[11px] h-5 font-semibold", RISK_STYLES[level], className)}>
            {humanize(level)}
        </Badge>
    );
}

export function PermissionBadge({ level, label }: { level?: PermissionLevel | null; label?: string | null }) {
    if (!level || level === "NONE") return null;
    return (
        <Badge variant="outline" className={cn("text-[11px] h-5 font-medium", PERMISSION_STYLES[level])}>
            {label || humanize(level)}
        </Badge>
    );
}

const TONE_STYLES: Record<ConnectionTone, { className: string; icon: ReactNode }> = {
    healthy: {
        className: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300",
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    },
    working: {
        className: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300",
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
    },
    warning: {
        className: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300",
        icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    error: {
        className: "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300",
        icon: <XCircle className="w-3.5 h-3.5" />,
    },
};

export function StatusPill({ tone, label, title }: { tone: ConnectionTone; label: string; title?: string }) {
    const style = TONE_STYLES[tone];
    return (
        <Badge variant="outline" title={title} className={cn("gap-1.5 h-6 text-xs font-medium whitespace-nowrap", style.className)}>
            {style.icon}
            {label}
        </Badge>
    );
}

export function BreakdownBars({
    items,
    labelFor = (item) => item.key,
    colorFor,
    onSelect,
    emptyLabel = "No activity in this period",
    showDistinct,
}: {
    items: BreakdownItem[];
    labelFor?: (item: BreakdownItem) => string;
    colorFor?: (item: BreakdownItem) => string;
    onSelect?: (item: BreakdownItem) => void;
    emptyLabel?: string;
    showDistinct?: string;
}) {
    if (items.length === 0) {
        return <p className="text-sm text-muted-foreground py-6 text-center">{emptyLabel}</p>;
    }
    const max = Math.max(...items.map((item) => item.eventCount), 1);
    return (
        <div className="space-y-1">
            {items.map((item) => (
                <div
                    key={item.key}
                    className={cn("rounded-lg px-2 py-1.5 portal-card-hover border border-transparent", onSelect && "cursor-pointer")}
                    onClick={() => onSelect?.(item)}
                >
                    <div className="flex items-center justify-between gap-3 text-xs mb-1">
                        <span className="font-medium truncate" title={labelFor(item)}>
                            {labelFor(item)}
                        </span>
                        <span className="flex items-center gap-2 flex-shrink-0 text-muted-foreground">
                            {item.highRiskCount > 0 && <span className="text-red-600 dark:text-red-400 font-medium">{formatNumber(item.highRiskCount)} high risk</span>}
                            {showDistinct && <span>{formatNumber(item.distinctCount)} {showDistinct}</span>}
                            <span className="font-semibold text-foreground">{formatNumber(item.eventCount)}</span>
                        </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden">
                        <div
                            className="h-full rounded-full"
                            style={{ width: `${(item.eventCount / max) * 100}%`, background: colorFor ? colorFor(item) : "hsl(var(--primary))" }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function ListToolbar({
    children,
    page,
    totalPages,
    onPageChange,
    total,
    pageSize,
    label,
}: {
    children?: ReactNode;
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    total: number;
    pageSize: number;
    label: string;
}) {
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return (
        <div className="resource-request-toolbar">
            <div className="saas-toolbar-group">{children}</div>
            <div className="resource-request-toolbar-pages">
                {total > 0 && first <= total && (
                    <span className="resource-request-toolbar-count">
                        Showing {formatNumber(first)}–{formatNumber(last)} of {formatNumber(total)}
                    </span>
                )}
                <CardPagination currentPage={page} totalPages={Math.max(1, totalPages)} onPageChange={onPageChange} label={label} />
            </div>
        </div>
    );
}

export function ToolbarSearch({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
    return (
        <div className="resource-request-toolbar-search">
            <Search className="resource-request-search-icon" />
            <Input aria-label={placeholder} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} className="pl-10" />
        </div>
    );
}

export function FilterField({ label, icon, children, className }: { label: string; icon: ReactNode; children: ReactNode; className?: string }) {
    return (
        <div className={cn("resource-request-filter", className)}>
            <label>
                {icon}
                {label}
            </label>
            {children}
        </div>
    );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
    return (
        <div className="space-y-2" role="status" aria-label="Loading">
            {Array.from({ length: rows }, (_, index) => (
                <Card key={index} className="cloud-card-surface animate-pulse rounded-2xl">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-8 h-8 bg-muted/60 rounded-full flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-muted/60 rounded w-1/4" />
                            <div className="h-3 bg-muted/60 rounded w-1/2" />
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <div className="text-[11px] font-medium text-muted-foreground mb-0.5">{label}</div>
            <div className="text-xs font-medium break-words">{children}</div>
        </div>
    );
}
